import { useState, useEffect } from "react";
import { IoInformationCircleOutline, IoArrowBack } from "react-icons/io5";
import { upgradePlan, getSubscriptionDetails } from "~/lib/network/billing";
import { useNavigate, useSearchParams, useBlocker } from "react-router";
import { DodoPayments } from "dodopayments-checkout";
import { useCoinUsageStore } from "~/stores/coin-usage-store";
import { useTheme } from "next-themes";
import { LuInfo } from "react-icons/lu";
import { VscError } from "react-icons/vsc";

interface OrderPreviewData {
  currentPlan: string;
  newPlan: string;
  newPrice: number;
  adjustment: number;
  subtotal: number;
  tax: number;
  total: number;
  renewalDate: string;
}

export default function OrderDetails() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { theme } = useTheme();
  const { fetchUserCoinUsage } = useCoinUsageStore();
  const rawPlan = (searchParams.get("plan") || "basic").toLowerCase();
  const allowedPlans = ["basic", "pro", "max"] as const;
  const targetPlan = (
    allowedPlans.includes(rawPlan as any)
      ? (rawPlan as (typeof allowedPlans)[number])
      : "basic"
  ) as "basic" | "pro" | "max";
  const returnRoute = searchParams.get("returnRoute") || "/";

  // Validate Dodo Payments mode from env
  const rawMode = import.meta.env.VITE_DODO_PAYMENTS_MODE || "test";
  const allowedModes = ["test", "live"] as const;
  const dodoPaymentsMode = (
    allowedModes.includes(rawMode as any) ? rawMode : "test"
  ) as "test" | "live";

  // Helper function to get plan usage description
  const getPlanUsageDescription = (plan: "basic" | "pro" | "max"): string => {
    const descriptions = {
      basic: "100 coins monthly",
      pro: "300 coins monthly",
      max: "700 coins monthly",
    };
    return descriptions[plan];
  };

  const [orderData, setOrderData] = useState<OrderPreviewData | null>(null);
  const [processing, setProcessing] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: "warning" | "error";
  } | null>(null);

  // Initialize Dodo Payments SDK and fetch subscription details
  useEffect(() => {
    // Initialize Dodo Payments SDK
    DodoPayments.Initialize({
      mode: dodoPaymentsMode,
      onEvent: (event: any) => {
        switch (event.event_type) {
          case "checkout.payment_page_opened":
            // Payment page has been displayed
            setProcessing(true);
            break;

          case "checkout.customer_details_submitted":
            // Customer and billing details submitted
            break;

          case "checkout.closed":
            // Checkout has been closed without completing payment
            setCheckoutOpen(false);
            setProcessing(false);
            break;

          case "checkout.redirect":
            // Checkout will perform a redirect - payment successful
            setCheckoutOpen(false);
            navigate(returnRoute);
            break;

          case "checkout.error":
            // An error occurred during checkout
            console.log("Checkout error occurred");
            setCheckoutOpen(false);
            setProcessing(false);
            break;

          default:
            console.log("Unknown checkout event:", event.event_type);
        }
      },
    });

    // Fetch order preview with subscription details
    const fetchOrderPreview = async () => {
      try {
        // Fetch subscription details with proration calculation
        const subscription = await getSubscriptionDetails(targetPlan);

        // Check if proration data exists
        if (!subscription.proration) {
          console.error("No proration data returned from API");
          return;
        }

        const proration = subscription.proration;

        // Format renewal date
        const renewalDate = proration.renewal_date
          ? new Date(proration.renewal_date).toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })
          : "";

        // Map proration data to order preview
        const orderPreview: OrderPreviewData = {
          currentPlan: proration.current_plan,
          newPlan: proration.new_plan,
          newPrice: proration.new_plan_price,
          adjustment: proration.adjustment,
          subtotal: proration.subtotal,
          tax: proration.tax,
          total: proration.total,
          renewalDate,
        };

        setOrderData(orderPreview);
      } catch (err) {
        console.error("Failed to load order preview");
      }
    };

    fetchOrderPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetPlan]);

  // Block navigation when checkout is open and close it instead
  useBlocker(() => {
    if (checkoutOpen) {
      DodoPayments.Checkout.close();
      setCheckoutOpen(false);
      setProcessing(false);
      return true; // Block the navigation
    }
    return false; // Allow navigation
  });

  // Auto-dismiss toast after 3 seconds
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => {
        setToast(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const handleConfirm = async () => {
    try {
      setProcessing(true);

      const response = await upgradePlan(
        targetPlan,
        theme === "dark" ? "dark" : "light"
      );

      if (response.payment_link) {
        // Use Dodo Payments overlay for event handling
        DodoPayments.Checkout.open({
          checkoutUrl: response.payment_link,
        });
        setCheckoutOpen(true);
      } else {
        setProcessing(false);
        navigate(returnRoute);
        fetchUserCoinUsage();
      }
    } catch (err) {
      setToast({
        message: "Failed to process upgrade. Please try again.",
        type: "error",
      });
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-base-100 items-center justify-center p-4">
      {/* Toast notification */}
      {toast && (
        <div className="toast toast-bottom toast-center z-50 mb-10">
          <div
            className={`alert ${
              toast.type === "error" ? "alert-error" : "alert-info"
            } alert-soft`}
          >
            <span className="flex items-center gap-2">
              {toast.type === "error" ? <VscError /> : <LuInfo />}
              {toast.message}
            </span>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => navigate(returnRoute)}
        className="btn btn-circle btn-soft btn-primary btn-sm mb-6"
      >
        <IoArrowBack className="w-4 h-4" />
      </button>
      <div className="m-auto max-w-xl w-full">
        {orderData ? (
          <>
            {/* Title */}
            <h1 className="text-3xl font-bold text-center mb-8">
              Change plan to{" "}
              <span className="text-info">{orderData.newPlan}</span>
            </h1>

            {/* Order details card */}
            <div className="card bg-base-200 shadow-xl">
              <div className="card-body">
                <h2 className="card-title text-lg mb-4">Order details</h2>

                {/* Plan details */}
                <div className="flex justify-between mb-4">
                  <div>
                    <p className="font-semibold">{orderData.newPlan}</p>
                    <p className="text-sm opacity-70">
                      {getPlanUsageDescription(targetPlan)}
                    </p>
                  </div>
                  <p className="font-semibold grow-0">
                    USD {orderData.newPrice.toFixed(0)}
                  </p>
                </div>

                {/* Adjustments */}
                <div className="flex justify-between mb-6 pb-6 border-b border-base-content/10">
                  <div>
                    <p className="font-semibold">Adjustments</p>
                    <p className="text-sm opacity-70">
                      Prorated credit for the remainder of{" "}
                      {orderData.currentPlan} plan
                    </p>
                  </div>
                  <p className="font-semibold grow-0">
                    -USD {Math.abs(orderData.adjustment).toFixed(2)}
                  </p>
                </div>

                {/* Subtotal */}
                <div className="flex justify-between mb-3">
                  <p>Subtotal</p>
                  <p className="font-semibold grow-0">
                    USD {orderData.subtotal.toFixed(2)}
                  </p>
                </div>

                {/* Total */}
                <div className="flex justify-between mb-6">
                  <p className="font-bold">Total due today</p>
                  <p className="font-bold grow-0">
                    USD {orderData.total.toFixed(2)}
                  </p>
                </div>

                {/* Renewal notice */}
                <div className="alert alert-soft mb-6">
                  <IoInformationCircleOutline className="w-5 h-5" />
                  <span className="text-sm">
                    Your subscription will auto-renew on {orderData.renewalDate}
                    . You will be charged USD {orderData.newPrice.toFixed(2)}
                    /month + tax.
                  </span>
                </div>

                {/* Terms checkbox */}
                <div className="form-control mb-6">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      className="checkbox checkbox-xs checkbox-primary mt-1"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                    />
                    <span className="text-sm opacity-70">
                      You agree that Gamgee will charge your card in the amount
                      above now and on a recurring monthly basis until you
                      cancel in accordance with our terms. You can cancel at any
                      time in your account settings.
                    </span>
                  </label>
                </div>

                {/* Subscribe button */}
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={!termsAccepted || processing}
                  className="btn btn-primary btn-block"
                >
                  {processing ? (
                    <span className="loading loading-spinner loading-sm"></span>
                  ) : (
                    "Subscribe"
                  )}
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Skeleton Title */}
            <div className="skeleton h-10 w-3/4 mx-auto mb-8"></div>

            {/* Skeleton Order details card */}
            <div className="card bg-base-200 shadow-xl">
              <div className="card-body">
                <div className="skeleton h-6 w-32 mb-4"></div>

                {/* Skeleton Plan details */}
                <div className="flex justify-between mb-4">
                  <div className="flex-1">
                    <div className="skeleton h-5 w-24 mb-2"></div>
                    <div className="skeleton h-4 w-40"></div>
                  </div>
                  <div className="skeleton h-5 w-16"></div>
                </div>

                {/* Skeleton Adjustments */}
                <div className="flex justify-between mb-6 pb-6 border-b border-base-content/10">
                  <div className="flex-1">
                    <div className="skeleton h-5 w-28 mb-2"></div>
                    <div className="skeleton h-4 w-56"></div>
                  </div>
                  <div className="skeleton h-5 w-20"></div>
                </div>

                {/* Skeleton Subtotal */}
                <div className="flex justify-between mb-3">
                  <div className="skeleton h-4 w-16"></div>
                  <div className="skeleton h-4 w-20"></div>
                </div>

                {/* Skeleton Tax */}
                <div className="flex justify-between mb-4 pb-4 border-b border-base-content/10">
                  <div className="skeleton h-4 w-12"></div>
                  <div className="skeleton h-4 w-16"></div>
                </div>

                {/* Skeleton Total */}
                <div className="flex justify-between mb-6">
                  <div className="skeleton h-5 w-32"></div>
                  <div className="skeleton h-5 w-20"></div>
                </div>

                {/* Skeleton Renewal notice */}
                <div className="skeleton h-16 w-full mb-6"></div>

                {/* Skeleton Terms checkbox */}
                <div className="skeleton h-16 w-full mb-6"></div>

                {/* Skeleton Subscribe button */}
                <div className="skeleton h-12 w-full"></div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
